import { useConsultsLeft } from "@/features/appointments/hooks/useConsultsLeft";
import { useCreateEvent } from "@/features/events/hooks/useCreateEvent";
import { EventType as NewEventType } from "@/features/events/types/EventType";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import 'react-toastify/dist/ReactToastify.css';
import { saveEvent } from "../../../utils/apis/user-service";
import { EventType } from "../../../utils/enums/EventType";
import { useAuthStore } from "../../../utils/stores/useAuthStore";
import { useConsultsStore } from "../../../utils/stores/useConsultsStore";
import { useExpertsStore } from "../../../utils/stores/useExpertsStore";
import { useUserStore } from "../../../utils/stores/useUserStore";
import { useIsMetabolicUser } from "../../metabolic-health/hooks/useMetabolicHealth";
import LoadingSpinner from "../../shared/components/Loaders";
import ContactExpertDialog from "../components/ContactExpertDialog";
import ExpertCard from "../components/ExpertCard";
import ExpertInfoDialog from "../components/ExpertInfoDialog";
import { CalendlyExpert } from "../types/CalendlyExpert";

const ExpertsPage = () => {
    const { sendEvent } = useCreateEvent()
    const { auth } = useAuthStore()
    const idToken = auth.idToken
    const { userData } = useUserStore()
    const email = userData.email
    const { experts } = useExpertsStore()
    const specialist = experts.specialist
    const nutritionist = experts.nutritionist
    const physician = experts.physician
    const { consultsData } = useConsultsStore()
    const { fetchConsultsLeft } = useConsultsLeft()
    const specialistConsultsLeft = consultsData.specialistConsultsLeft
    const nutritionistConsultsLeft = consultsData.nutritionistConsultsLeft
    const isMetabolicUser = useIsMetabolicUser()

    const [shouldShowExpertInfoDialog, setShouldShowExpertInfoDialog] = useState(false)
    const [shouldShowContactDialog, setShouldShowContactDialog] = useState(false)
    const [expertToFocusOn, setExpertToFocusOn] = useState<CalendlyExpert | null>(null)
    const [expandedExpertId, setExpandedExpertId] = useState<string | null>(null)

    const [isLoading, setIsLoading] = useState(false)

    const navigate = useNavigate()

    const toggleDialog = (dialogType: 'expertInfo' | 'contact', expert?: CalendlyExpert) => {
        setExpertToFocusOn(expert || null);
        if (dialogType === 'expertInfo') {
            setShouldShowExpertInfoDialog(prev => !prev);
            setShouldShowContactDialog(false);
        } else {
            setShouldShowContactDialog(prev => !prev);
            setShouldShowExpertInfoDialog(false);
        }
    };

    const handleExpertExpand = (expert: CalendlyExpert) => {
        setExpandedExpertId(prevId => prevId === expert.id ? null : expert.id);
    };

    const onExpertInfoClick = async (expert: CalendlyExpert) => {
        toggleDialog('expertInfo', expert);
        if (!email) return
        saveEvent(
            email,
            "expert_info_button_clicked",
            (success) => { console.log() },
            (error) => { console.log(error) },
            "equipe_page",
        )

        await sendEvent({
            event: NewEventType.BUTTON_CLICK,
            sub_category: "action_button",
            event_metadata: { action: "expert_info", expert: expert.role }
        })
    }

    const onContactClick = async (expert: CalendlyExpert) => {
        toggleDialog('contact', expert);
        if (!email) return
        saveEvent(
            email,
            "contact_expert_button_clicked",
            (success) => { console.log() },
            (error) => { console.log(error) },
            "equipe_page",
        )

        await sendEvent({
            event: NewEventType.BUTTON_CLICK,
            sub_category: "action_button",
            event_metadata: { action: "contact_expert", expert: expert.role }
        })
    }

    const closeDialogs = () => {
        setShouldShowExpertInfoDialog(false);
        setShouldShowContactDialog(false);
    };

    const onBookClick = async (expert: CalendlyExpert) => {
        if (!email) return;
        
        await saveEvent(
            email,
            "book_consult_button_clicked",
            (success) => { console.log() },
            (error) => { console.log(error) },
            "equipe_page",
        );

        await sendEvent({
            event: NewEventType.BUTTON_CLICK,
            sub_category: "action_button",
            event_metadata: { action: "book_consult", expert: expert.role }
        });

        // Refresh consults left after booking
        await fetchConsultsLeft();
    };

    useEffect(() => {
        if (!email) return
        saveEvent(
            email,
            EventType.PageView,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "your_experts_page",
        )

        void sendEvent({
            event: NewEventType.PAGE_VIEW,
            sub_category: "experts_page",
        })
    }, [])

    useEffect(() => {
        fetchConsultsLeft()
    }, [])

    return (
        <div className={`w-full h-full flex flex-col max-w-[1068px]`}>
            <div className={`w-full h-full flex flex-col space-y-[20px] ${(shouldShowExpertInfoDialog || shouldShowContactDialog) && "opacity-50 pointer-events-none"}`}>
                <div className="font-semibold text-[12px] leading-[20px]">
                    Per riscattare e gestire gli acquisti con questo account assicurati di utilizzare la mail <span className="text-holifya-blue">{email}</span> al momento dell'acquisto.
                </div>
                {
                    specialistConsultsLeft !== null && specialistConsultsLeft > 0 && specialist &&
                    <ExpertCard
                        expert={isMetabolicUser ? physician! : specialist}
                        onExpertInfoClick={onExpertInfoClick}
                        onContactClick={onContactClick}
                        onBookClick={onBookClick}
                        isExpanded={expandedExpertId === (isMetabolicUser ? physician! : specialist).id}
                        onExpandChange={handleExpertExpand}
                    />
                }
                {
                    nutritionistConsultsLeft !== null && nutritionist &&
                    <ExpertCard
                        expert={nutritionist}
                        onExpertInfoClick={onExpertInfoClick}
                        onContactClick={onContactClick}
                        onBookClick={onBookClick}
                        isExpanded={expandedExpertId === nutritionist.id}
                        onExpandChange={handleExpertExpand}
                    />
                }
                <div className="h-[20px] flex-shrink-0" />
            </div>
            {
                shouldShowExpertInfoDialog && expertToFocusOn &&
                <ExpertInfoDialog
                    expert={expertToFocusOn}
                    onExpertInfoDismissClick={closeDialogs}
                />
            }
            {
                shouldShowContactDialog && expertToFocusOn &&
                <ContactExpertDialog
                    expert={expertToFocusOn}
                    onCloseClick={closeDialogs}
                />
            }
            {isLoading && <div className="w-full h-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col justify-center items-center"><LoadingSpinner /></div>}
        </div>
    )
}

export default ExpertsPage
